import { MarkerType } from "react-flow-renderer";
import { getCounterpartIcon } from "./flowCards";

const CENTER_NODE_ID = "data-flow-center";
const COLUMN_GAP = 360;
const ROW_GAP = 96;
const EDGE_STROKE = "#CBD0E6";

const counterpartKey = (card) =>
    `${card.counterpart?.kind || "unknown"}-${card.counterpart?.id}`;

const formatEdgeLabel = (
    card,
    { methodLabels = {}, typeLabels = {}, relationLabels = {}, t }
) => {
    if (card.relation) {
        return t(relationLabels[card.relation] || card.relation);
    }

    const method = card.methods?.[0];
    const dataType = card.dataTypes?.[0];
    const parts = [
        method ? t(methodLabels[method] || method) : null,
        dataType ? t(typeLabels[dataType] || dataType) : null,
    ].filter(Boolean);

    return parts.join(" + ");
};

const collectSide = (cards, side, labelMaps) => {
    const nodes = new Map();

    (cards || []).forEach((card) => {
        const counterpart = card.counterpart;

        if (!counterpart?.id) {
            return;
        }

        const key = `${side}-${counterpartKey(card)}`;
        const existing = nodes.get(key);
        const label = formatEdgeLabel(card, labelMaps);

        if (existing) {
            if (label && !existing.labels.includes(label)) {
                existing.labels.push(label);
            }

            if (card.relation) {
                existing.isRelation = true;
            }

            return;
        }

        nodes.set(key, {
            id: key,
            name: counterpart.name || "—",
            side,
            isHardware: counterpart.type === "hardware",
            isRelation: Boolean(card.relation),
            icon: getCounterpartIcon(counterpart),
            labels: label ? [label] : [],
        });
    });

    return Array.from(nodes.values());
};

const buildEdge = ({ id, source, target, labels, isRelation }) => {
    return {
        id,
        source,
        target,
        label: labels.join(", ") || undefined,
        type: "default",
        markerEnd: {
            type: MarkerType.ArrowClosed,
            color: EDGE_STROKE,
            width: 14,
            height: 14,
        },
        style: {
            stroke: EDGE_STROKE,
            strokeWidth: 1.5,
            strokeDasharray: isRelation ? "6 4" : undefined,
        },
        labelStyle: {
            fill: "#5B6478",
            fontSize: 10,
            fontWeight: 500,
        },
        labelBgStyle: {
            fill: "#FFFFFF",
            stroke: "#E7E9F5",
        },
        labelBgPadding: [6, 4],
        labelBgBorderRadius: 4,
    };
};

const buildVisualMapGraph = ({
    softwareName,
    incomingCards = [],
    outgoingCards = [],
    methodLabels,
    typeLabels,
    relationLabels,
    t,
}) => {
    const labelMaps = { methodLabels, typeLabels, relationLabels, t };
    const incoming = collectSide(incomingCards, "incoming", labelMaps);
    const outgoing = collectSide(outgoingCards, "outgoing", labelMaps);

    if (!incoming.length && !outgoing.length) {
        return null;
    }

    const rows = Math.max(incoming.length, outgoing.length, 1);
    const canvasHeight = (rows - 1) * ROW_GAP;
    const columnOffset = (count) =>
        count ? (canvasHeight - (count - 1) * ROW_GAP) / 2 : canvasHeight / 2;

    const nodes = [
        {
            id: CENTER_NODE_ID,
            type: "softwareFlow",
            position: { x: COLUMN_GAP, y: canvasHeight / 2 },
            data: {
                label: softwareName || "—",
                caption: "Central Software Asset",
                variant: "center",
                side: "center",
                icon: "ri-sparkling-line",
            },
        },
    ];

    incoming.forEach((item, index) => {
        nodes.push({
            id: item.id,
            type: "softwareFlow",
            position: {
                x: 0,
                y: columnOffset(incoming.length) + index * ROW_GAP,
            },
            data: {
                label: item.name,
                caption: item.isHardware ? "Hardware" : "Incoming",
                variant: item.isHardware ? "hardware" : "incoming",
                side: "incoming",
                icon: item.icon,
                tooltip: [item.name, ...item.labels].join(" · "),
            },
        });
    });

    outgoing.forEach((item, index) => {
        nodes.push({
            id: item.id,
            type: "softwareFlow",
            position: {
                x: COLUMN_GAP * 2,
                y: columnOffset(outgoing.length) + index * ROW_GAP,
            },
            data: {
                label: item.name,
                caption: item.isHardware ? "Hardware" : "Outgoing",
                variant: item.isHardware ? "hardware" : "outgoing",
                side: "outgoing",
                icon: item.icon,
                tooltip: [item.name, ...item.labels].join(" · "),
            },
        });
    });

    const edges = [
        ...incoming.map((item) =>
            buildEdge({
                id: `edge-${item.id}`,
                source: item.id,
                target: CENTER_NODE_ID,
                labels: item.labels,
                isRelation: item.isRelation,
            })
        ),
        ...outgoing.map((item) =>
            buildEdge({
                id: `edge-${item.id}`,
                source: CENTER_NODE_ID,
                target: item.id,
                labels: item.labels,
                isRelation: item.isRelation,
            })
        ),
    ];

    return { nodes, edges };
};

export default buildVisualMapGraph;
